import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LogOut, Plus, Edit, Trash2, Package, Folder, Settings as SettingsIcon, X, Eye } from 'lucide-react';

const AdminDashboard = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const navigate = useNavigate();

  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category_id: '',
    images: [], video: '', youtube_link: '', instagram_link: '', status: 'draft'
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const [settingsForm, setSettingsForm] = useState({ whatsapp_number: '', company_logo: '', company_name: '' });

  useEffect(() => { verifyToken(); fetchSettings(); fetchCategories(); fetchProducts(); }, []);

  useEffect(() => { fetchProducts(); }, [statusFilter]);

  const verifyToken = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    try {
      await axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } });
    } catch { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out');
    navigate('/');
  };

  // Categories
  const fetchCategories = async () => {
    try { const r = await axios.get(`${API}/categories`); setCategories(r.data); }
    catch (e) { console.error('Error fetching categories:', e); }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, { headers: getAuthHeaders() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, categoryForm, { headers: getAuthHeaders() });
        toast.success('Category created');
      }
      setCategoryDialogOpen(false);
      setCategoryForm({ name: '', description: '' });
      setEditingCategory(null);
      fetchCategories();
    } catch { toast.error('Error saving category'); }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeaders() });
      toast.success('Category deleted');
      fetchCategories();
      fetchProducts();
    } catch { toast.error('Error deleting category'); }
  };

  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, description: category.description || '' });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '' });
    }
    setCategoryDialogOpen(true);
  };

  // Products
  const fetchProducts = async () => {
    try {
      let url = `${API}/products`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      const r = await axios.get(url);
      setProducts(r.data);
    } catch (e) { console.error('Error fetching products:', e); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (productForm.images.length >= 5) {
      toast.error('Maximum 5 images allowed');
      e.target.value = '';
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/upload-image`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      if (r.data.success) {
        setProductForm(prev => ({ ...prev, images: [...prev.images, r.data.url] }));
        toast.success(`Uploaded: ${file.name}`);
      }
    } catch { toast.error(`Failed to upload ${file.name}`); }
    e.target.value = '';
  };

  const removeImage = (index) => {
    setProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      toast.info('Uploading video...');
      const r = await axios.post(`${API}/upload-video`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      if (r.data.success) {
        setProductForm(prev => ({ ...prev, video: r.data.url }));
        toast.success('Video uploaded');
      }
    } catch { toast.error('Failed to upload video'); }
    e.target.value = '';
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...productForm, price: parseFloat(productForm.price) };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, submitData, { headers: getAuthHeaders() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, submitData, { headers: getAuthHeaders() });
        toast.success('Product created');
      }
      setProductDialogOpen(false);
      setProductForm({ name: '', description: '', price: '', category_id: '', images: [], video: '', youtube_link: '', instagram_link: '', status: 'draft' });
      setEditingProduct(null);
      fetchProducts();
    } catch { toast.error('Error saving product'); }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/products/${id}`, { headers: getAuthHeaders() });
      toast.success('Product deleted');
      fetchProducts();
    } catch { toast.error('Error deleting product'); }
  };

  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name, description: product.description || '',
        price: product.price.toString(), category_id: product.category_id,
        images: product.images || [], video: product.video || '',
        youtube_link: product.youtube_link || '', instagram_link: product.instagram_link || '',
        status: product.status || 'draft'
      });
    } else {
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', category_id: '', images: [], video: '', youtube_link: '', instagram_link: '', status: 'draft' });
    }
    setProductDialogOpen(true);
  };

  const toggleProductStatus = async (product) => {
    try {
      const newStatus = product.status === 'published' ? 'draft' : 'published';
      await axios.put(`${API}/products/${product.id}`, { ...product, status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Product ${newStatus === 'published' ? 'published' : 'moved to draft'}`);
      fetchProducts();
    } catch { toast.error('Error updating status'); }
  };

  // Settings
  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${API}/settings`);
      setSettings(r.data);
      setSettingsForm({
        whatsapp_number: r.data.whatsapp_number || '',
        company_logo: r.data.company_logo || '',
        company_name: r.data.company_name || ''
      });
    } catch (e) { console.error('Error fetching settings:', e); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/upload-image`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      if (r.data.success) {
        setSettingsForm(prev => ({ ...prev, company_logo: r.data.url }));
        toast.success('Logo uploaded');
      }
    } catch { toast.error('Failed to upload logo'); }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, settingsForm, { headers: getAuthHeaders() });
      toast.success('Settings updated');
      fetchSettings();
    } catch { toast.error('Error updating settings'); }
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : 'Unknown';
  };

  const getImgSrc = (img) => {
    if (img.startsWith('data:')) return img;
    if (img.includes('/uploads/')) {
      const filename = img.split('/uploads/').pop();
      return `${API.replace('/api', '')}/uploads/${filename}`;
    }
    return `${API.replace('/api', '')}/uploads/${img}`;
  };

  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Outfit:wght@300;400;500;600;700&display=swap');`}</style>

      {/* Header */}
      <header className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Syne, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-sm text-stone-500">Manage your Badgeify catalogue</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/')} className="border-stone-200 text-stone-600 hover:bg-stone-50">
              <Eye className="mr-2 h-4 w-4" />
              View Site
            </Button>
            <Button variant="destructive" onClick={handleLogout} className="bg-red-500 hover:bg-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-stone-200 mb-8">
            <TabsTrigger value="products" className="data-[state=active]:bg-stone-900 data-[state=active]:text-white">
              <Package className="mr-2 h-4 w-4" /> Products
            </TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-stone-900 data-[state=active]:text-white">
              <Folder className="mr-2 h-4 w-4" /> Categories
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-stone-900 data-[state=active]:text-white">
              <SettingsIcon className="mr-2 h-4 w-4" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-stone-900">Products</h2>
              <div className="flex items-center gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px] bg-white">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Drafts</SelectItem>
                  </SelectContent>
                </Select>
                <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => openProductDialog()} className="bg-stone-900 hover:bg-stone-800">
                      <Plus className="mr-2 h-4 w-4" /> Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                      <DialogDescription>Fill in the product details below</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleProductSubmit} className="space-y-4">
                      <div>
                        <Label>Product Name</Label>
                        <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required rows={5} />
                        <p className="text-xs text-stone-500 mt-1">Use **bold**, *italic*, - for bullets</p>
                      </div>
                      <div>
                        <Label>Price (INR)</Label>
                        <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={productForm.category_id} onValueChange={(v) => setProductForm({ ...productForm, category_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            {categories.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Product Video (optional, max 1)</Label>
                        {productForm.video ? (
                          <div className="flex items-center gap-2 mt-1 mb-2 p-2 bg-stone-50 rounded border">
                            <video src={getImgSrc(productForm.video)} className="h-16 rounded" />
                            <span className="text-xs text-stone-500 flex-1 truncate">{productForm.video.split('/').pop()}</span>
                            <Button type="button" variant="destructive" size="sm" onClick={() => setProductForm(prev => ({ ...prev, video: '' }))}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <Input type="file" accept="video/*" onChange={handleVideoUpload} className="cursor-pointer" />
                        )}
                      </div>
                      <div>
                        <Label>YouTube Link (optional)</Label>
                        <Input value={productForm.youtube_link} onChange={(e) => setProductForm({ ...productForm, youtube_link: e.target.value })} placeholder="https://youtube.com/watch?v=..." />
                      </div>
                      <div>
                        <Label>Instagram Link (optional)</Label>
                        <Input value={productForm.instagram_link} onChange={(e) => setProductForm({ ...productForm, instagram_link: e.target.value })} placeholder="https://www.instagram.com/reel/..." />
                      </div>
                      <div>
                        <Label>Product Images (max 5)</Label>
                        <Input type="file" accept="image/*" onChange={handleImageUpload} className="cursor-pointer" disabled={productForm.images.length >= 5} />
                        <p className="text-xs text-stone-500 mt-1">{productForm.images.length}/5 images uploaded</p>
                        {productForm.images.length > 0 && (
                          <div className="grid grid-cols-5 gap-2 mt-3">
                            {productForm.images.map((img, idx) => (
                              <div key={idx} className="relative group">
                                <img src={getImgSrc(img)} alt={`Preview ${idx + 1}`} className="w-full h-20 object-contain rounded bg-stone-50 border" onError={(e) => { e.target.style.display = 'none'; }} />
                                <button type="button" onClick={() => removeImage(idx)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select value={productForm.status} onValueChange={(v) => setProductForm({ ...productForm, status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="bg-stone-900 hover:bg-stone-800">
                          {editingProduct ? 'Update' : 'Create'} Product
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map(product => (
                <Card key={product.id} className="bg-white border-stone-200 overflow-hidden">
                  <div className="relative h-40 bg-stone-100">
                    {product.images && product.images.length > 0 ? (
                      <img src={getImgSrc(product.images[0])} alt={product.name} className="w-full h-full object-contain p-2" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-12 w-12 text-stone-300" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${product.status === 'published' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-amber-100 text-amber-700 border border-amber-200'}`}>
                        {product.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{product.name}</CardTitle>
                    <CardDescription className="text-xs">{getCategoryName(product.category_id)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg font-bold text-stone-900 mb-3">₹{product.price.toLocaleString('en-IN')}</p>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <Button onClick={() => openProductDialog(product)} variant="outline" size="sm" className="flex-1">
                          <Edit className="mr-1 h-3 w-3" /> Edit
                        </Button>
                        <Button onClick={() => toggleProductStatus(product)} variant={product.status === 'published' ? 'secondary' : 'default'} size="sm" className="flex-1">
                          {product.status === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm"><Trash2 className="mr-1 h-3 w-3" /> Delete</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Product</AlertDialogTitle>
                            <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteProduct(product.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-stone-900">Categories</h2>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => openCategoryDialog()} className="bg-stone-900 hover:bg-stone-800">
                    <Plus className="mr-2 h-4 w-4" /> Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
                    <DialogDescription>Manage product categories</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCategorySubmit} className="space-y-4">
                    <div>
                      <Label>Category Name</Label>
                      <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required />
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} rows={3} />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-stone-900 hover:bg-stone-800">
                        {editingCategory ? 'Update' : 'Create'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map(category => (
                <Card key={category.id} className="bg-white border-stone-200">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Folder className="mr-2 h-5 w-5 text-amber-600" />
                      {category.name}
                    </CardTitle>
                    {category.description && <CardDescription>{category.description}</CardDescription>}
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button onClick={() => openCategoryDialog(category)} variant="outline" size="sm" className="flex-1">
                        <Edit className="mr-1 h-3 w-3" /> Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm" className="flex-1">
                            <Trash2 className="mr-1 h-3 w-3" /> Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Category</AlertDialogTitle>
                            <AlertDialogDescription>This will also delete all products in this category.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteCategory(category.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="max-w-2xl mx-auto bg-white border-stone-200">
              <CardHeader>
                <CardTitle>Application Settings</CardTitle>
                <CardDescription>Configure your catalogue settings</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSettingsSubmit} className="space-y-6">
                  <div>
                    <Label>Company Name</Label>
                    <Input value={settingsForm.company_name} onChange={(e) => setSettingsForm({ ...settingsForm, company_name: e.target.value })} placeholder="e.g., Badgeify" />
                  </div>
                  <div>
                    <Label>WhatsApp Number</Label>
                    <Input value={settingsForm.whatsapp_number} onChange={(e) => setSettingsForm({ ...settingsForm, whatsapp_number: e.target.value })} placeholder="918889915080 (with country code, no +)" />
                    <p className="text-xs text-stone-500 mt-1">Enter with country code, without + symbol</p>
                  </div>
                  <div>
                    <Label>Company Logo</Label>
                    <Input type="file" accept="image/*" onChange={handleLogoUpload} className="cursor-pointer" />
                    {settingsForm.company_logo && (
                      <div className="mt-3">
                        <img src={getImgSrc(settingsForm.company_logo)} alt="Logo preview" className="h-20 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>
                  <Button type="submit" className="bg-stone-900 hover:bg-stone-800">Save Settings</Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
